import { lazy } from 'react';
import { Navigate } from 'react-router-dom';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';

// Dashboard & Layout
const Default = Loadable(lazy(() => import('views/dashboard/default')));
const KanbanBoard = Loadable(lazy(() => import('views/dashboard/personal/kanban')));
const CalendarBoard = Loadable(lazy(() => import('views/dashboard/personal/calendar')));
const Profile = Loadable(lazy(() => import('views/profile')));

// Work Management
const BusinessOpportunityAll = Loadable(lazy(() => import('views/workmgmt/busopp-all')));
const BusinessOpportunity = Loadable(lazy(() => import('views/workmgmt/busopp')));
const BusinessOpportunityEdit = Loadable(lazy(() => import('views/workmgmt/busopp/EditBusOpp')));
const BusinessOpportunityAdd = Loadable(lazy(() => import('views/workmgmt/busopp/AddBusOpp')));
const WorkOrderAll = Loadable(lazy(() => import('views/workmgmt/workorder-all')));
const WorkOrder = Loadable(lazy(() => import('views/workmgmt/workorder')));
const WorkOrderEdit = Loadable(lazy(() => import('views/workmgmt/workorder/EditWorkOrder')));
const WorkOrderAdd = Loadable(lazy(() => import('views/workmgmt/workorder/AddWorkOrder')));
const WorkOrderAddFromBusinessOpportunity = Loadable(lazy(() => import('views/workmgmt/workorder/AddWorkOrderFromBusOpp')));
const TicketAdd = Loadable(lazy(() => import('views/workmgmt/tickets/add')));
const Ticket = Loadable(lazy(() => import('views/workmgmt/tickets')));

// Admin - CRM
const Client = Loadable(lazy(() => import('views/admin/crm/client')));
const ClientEdit = Loadable(lazy(() => import('views/admin/crm/client/edit')));
const ClientAdd = Loadable(lazy(() => import('views/admin/crm/client/add')));
const Contract = Loadable(lazy(() => import('views/admin/crm/contract')));

// Admin - HR
const Department = Loadable(lazy(() => import('views/admin/hr/department')));
const Employee = Loadable(lazy(() => import('views/admin/hr/employee')));

// Admin - Template Management
const TplBusinessOpportunity = Loadable(lazy(() => import('views/admin/tplmgmt/busopp')));
const TplWorkOrder = Loadable(lazy(() => import('views/admin/tplmgmt/workorder')));
const TplFollowUp = Loadable(lazy(() => import('views/admin/tplmgmt/followup')));

// Admin - Nomenclature
const ContractType = Loadable(lazy(() => import('views/system/nomcltr/contract-type')));
const PartnershipType = Loadable(lazy(() => import('views/system/nomcltr/partnership-type')));
const BusinessOpportunityType = Loadable(lazy(() => import('views/system/nomcltr/busopp-type')));
const WorkOrderType = Loadable(lazy(() => import('views/system/nomcltr/workorder-type')));
const TicketType = Loadable(lazy(() => import('views/system/nomcltr/ticket-type')));

// System - Security
const User = Loadable(lazy(() => import('views/system/security/user')));
const Group = Loadable(lazy(() => import('views/system/security/group')));

// System - Log
const LogBusOppArchive = Loadable(lazy(() => import('views/system/log/busopp-archive')));
const LogWorkOrderArchive = Loadable(lazy(() => import('views/system/log/workorder-archive')));
const LogOperation = Loadable(lazy(() => import('views/system/log/operation')));
const LogCommandExecution = Loadable(lazy(() => import('views/system/log/command-execution')));

// System - Log View Pages
const LogBusOppArchiveViewPage = Loadable(lazy(() => import('views/system/log/busopp-archive/view/busopp-report')));
const LogWorkOrderArchiveViewPage = Loadable(lazy(() => import('views/system/log/workorder-archive/view/workorder-report')));
const LogOperationPage = Loadable(lazy(() => import('views/system/log/operation')));

// System - Settings
const AppSettings = Loadable(lazy(() => import('views/system/app-setting')));

// ==============================|| MAIN ROUTING ||============================== //
// *** IMPORTANT ***
// This component as well as its name are mandatory, in order for the theme to work correctly

const mainChildren = [
    { index: true, element: <Navigate to="/dashboard/default" replace /> },
    // Dashboard & Layout
    { path: '/dashboard/default', element: <Default /> }, // <-- Important: This route CANNOT be protected by authorizations!
    { path: '/dashboard/personal/kanban', element: <KanbanBoard />, requiredAuthorizations: ['dashboard-personal-kanban-canView'] },
    { path: '/dashboard/personal/calendar', element: <CalendarBoard />, requiredAuthorizations: ['dashboard-personal-calendar-canView'] },
    { path: '/profile', element: <Profile /> },
    // Work Management
    { path: '/workmgmt/busopp/list', element: <BusinessOpportunityAll />, requiredAuthorizationPattern: /^workmgmt-busopp-.+$/ },
    { path: '/workmgmt/busopp/:busopptype/list', element: <BusinessOpportunity />, requiredAuthorizationPattern: /^workmgmt-busopp-.+$/ },
    {
        path: '/workmgmt/busopp/:busopptype/edit/:id',
        element: <BusinessOpportunityEdit />,
        requiredAuthorizationPattern: /^workmgmt-busopp-.+-canEdit$/
    },
    {
        path: '/workmgmt/busopp/:busopptype/create',
        element: <BusinessOpportunityAdd />,
        requiredAuthorizationPattern: /^workmgmt-busopp-.+-canAdd$/
    },
    { path: '/workmgmt/workorder/list', element: <WorkOrderAll />, requiredAuthorizationPattern: /^workmgmt-workorder-.+$/ },
    { path: '/workmgmt/workorder/:workordertype/list', element: <WorkOrder />, requiredAuthorizationPattern: /^workmgmt-workorder-.+$/ },
    {
        path: '/workmgmt/workorder/:workordertype/edit/:id',
        element: <WorkOrderEdit />,
        requiredAuthorizationPattern: /^workmgmt-workorder-.+-canEdit$/
    },
    {
        path: '/workmgmt/workorder/:workordertype/create',
        element: <WorkOrderAdd />,
        requiredAuthorizationPattern: /^workmgmt-workorder-.+-canAdd$/
    },
    {
        path: '/workmgmt/workorder/:workordertype/createfrom/:busopptype/:busoppid',
        element: <WorkOrderAddFromBusinessOpportunity />,
        requiredAuthorizationPattern: /^workmgmt-workorder-.+-canAdd$/
    },
    { path: '/workmgmt/tickets/create', element: <TicketAdd />, requiredAuthorizations: ['workmgmt-ticket-create-canAdd'] },
    { path: '/workmgmt/tickets/list', element: <Ticket />, requiredAuthorizationPattern: /^workmgmt-ticket-.+$/ },
    {
        path: '/workmgmt/tickets/listall',
        element: <Navigate to="/workmgmt/tickets/list" replace />,
        requiredAuthorizationPattern: /^workmgmt-ticket-.+$/
    },
    // Admin - CRM
    { path: '/admin/crm/client/list', element: <Client />, requiredAuthorizationPattern: /^admin-crm-client-.+$/ },
    { path: '/admin/crm/client/edit/:id', element: <ClientEdit />, requiredAuthorizations: ['admin-crm-client-canEdit'] },
    { path: '/admin/crm/client/create', element: <ClientAdd />, requiredAuthorizations: ['admin-crm-client-canAdd'] },
    { path: '/admin/crm/contract/list', element: <Contract />, requiredAuthorizationPattern: /^admin-crm-contract-.+$/ },
    // Admin - HR
    { path: '/admin/hr/employee/list', element: <Employee />, requiredAuthorizationPattern: /^admin-hr-employee-.+$/ },
    { path: '/admin/hr/department/list', element: <Department />, requiredAuthorizationPattern: /^admin-hr-department-.+$/ },
    // Admin - Template Management
    { path: '/admin/tplmgmt/busopp/list', element: <TplBusinessOpportunity />, requiredAuthorizationPattern: /^admin-tplmgmt-busopp-.+$/ },
    { path: '/admin/tplmgmt/workorder/list', element: <TplWorkOrder />, requiredAuthorizationPattern: /^admin-tplmgmt-workorder-.+$/ },
    { path: '/admin/tplmgmt/followup/list', element: <TplFollowUp />, requiredAuthorizationPattern: /^admin-tplmgmt-followup-.+$/ },
    // Admin - Nomenclature
    {
        path: '/system/nomcltr/contract-type/list',
        element: <ContractType />,
        requiredAuthorizationPattern: /^system-nomcltr-contracttype-.+$/
    },
    {
        path: '/system/nomcltr/busopp-type/list',
        element: <BusinessOpportunityType />,
        requiredAuthorizationPattern: /^system-nomcltr-busopptype-.+$/
    },
    {
        path: '/system/nomcltr/workorder-type/list',
        element: <WorkOrderType />,
        requiredAuthorizationPattern: /^system-nomcltr-workordertype-.+$/
    },
    { path: '/system/nomcltr/ticket-type/list', element: <TicketType />, requiredAuthorizationPattern: /^system-nomcltr-tickettype-.+$/ },
    {
        path: '/system/nomcltr/partnership-type/list',
        element: <PartnershipType />,
        requiredAuthorizationPattern: /^system-nomcltr-partnershiptype-.+$/
    },
    // System - Security
    { path: '/system/security/user/list', element: <User />, requiredAuthorizationPattern: /^system-security-user-.+$/ },
    { path: '/system/security/group/list', element: <Group />, requiredAuthorizationPattern: /^system-security-group-.+$/ },
    // System - Log
    {
        path: '/system/log/busopp-archive/list',
        element: <LogBusOppArchive />,
        requiredAuthorizationPattern: /^system-log-busopparchive-.+$/
    },
    {
        path: '/system/log/workorder-archive/list',
        element: <LogWorkOrderArchive />,
        requiredAuthorizationPattern: /^system-log-workorderarchive-.+$/
    },
    { path: '/system/log/operation/list', element: <LogOperation />, requiredAuthorizationPattern: /^system-log-operation-.+$/ },
    {
        path: '/system/log/command-execution/list',
        element: <LogCommandExecution />,
        requiredAuthorizationPattern: /^system-log-commandexecution-.+$/
    },
    // System - Log - Report Views
    {
        path: '/system/log/busopp-archive/:id',
        element: <LogBusOppArchiveViewPage />,
        requiredAuthorizations: ['system-log-busopparchive-canView']
    },
    {
        path: '/system/log/workorder-archive/:id',
        element: <LogWorkOrderArchiveViewPage />,
        requiredAuthorizations: ['system-log-workorderarchive-canView']
    },
    { path: '/system/log/operation/:id', element: <LogOperationPage />, requiredAuthorizations: ['system-log-operation-canView'] },
    // System - Settings
    {
        path: '/admin/appsettings/',
        element: <AppSettings />,
        requiredAuthorizationPattern: /^system-(appsettings|codegeneratorsettings)-.+$/
    },
    // Fallback
    { path: '*', element: <Navigate to="/dashboard/default" replace /> }
];

const MainRoutes = {
    path: '/',
    element: (
        <AuthGuard routes={mainChildren}>
            <MainLayout />
        </AuthGuard>
    ),
    children: mainChildren
};

export default MainRoutes;
